import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { Card, Button, Badge, Container, Row, Col } from "react-bootstrap";
import { 
  PermIdentity, 
  Print, 
  ArrowBack, 
  Phone, 
  Person, 
  LocalHospital,
  AccessTime,
  Assignment
} from "@mui/icons-material";
import { toast } from "react-toastify";
import Loader from "../Loader";
import FilePreview from "../Vehicles/FilePreview";
import { getIncidentTypeText, getTreatmentTypeText, INCIDENT_TYPE, TREATMENT_TYPES } from "../enum";

const ViewPatient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const printableRef = useRef(null);
  const [error, setError] = useState(null);

  // Fetch patient data
  const fetchPatient = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(`/patients/${id}`);
      setPatient(response.data);
      setLoading(false);
    } catch (error) {
      setError(error);
      toast.error("Failed to load patient details.", {
        position: "top-right",
        autoClose: 1000,
      });
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (printableRef.current) {
      const printContent = printableRef.current.innerHTML;
      const originalContent = document.body.innerHTML;
      document.body.innerHTML = printContent;
      window.print();
      document.body.innerHTML = originalContent;
      window.location.reload();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date detected:', dateString);
        return 'Invalid Date';
      }
      
      // Check if date is reasonable (after 1900 and before 2100)
      const year = date.getFullYear();
      if (year < 1900 || year > 2100) {
        console.warn('Date out of reasonable range:', dateString, 'Year:', year);
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, 'Input:', dateString);
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'active': 'success',
      'completed': 'primary',
      'pending': 'warning',
      'cancelled': 'danger'
    };
    return statusColors[status?.toLowerCase()] || 'secondary';
  };

  const getFullName = (p) => {
    if (!p) return "";
    return `${p.firstName || ""} ${p.middleName || ""} ${p.lastName || ""}`.replace(/\s+/g, " ").trim();
  };

  const formatFieldValue = (key, value) => {
    if (value === null || value === undefined || value === "") return "N/A";

    if (key === "gender") {
      return value === 101 ? "Male" : value === 102 ? "Female" : String(value);
    }

    if (key === "incidentType") {
      return INCIDENT_TYPE.OTHER === value ? (patient?.incidentTypeOther || "Other") : getIncidentTypeText(value);
    }

    if (key === "firstResponderTreatments") {
      return TREATMENT_TYPES.OTHER === value
        ? (patient?.firstResponderOtherTreatment || "Other")
        : getTreatmentTypeText(value);
    }

    if (key === "ambulanceTreatments") {
      return TREATMENT_TYPES.OTHER === value
        ? (patient?.ambulanceOtherTreatment || "Other")
        : getTreatmentTypeText(value);
    }

    if (key === "createdAt" || key === "updatedAt") {
      return formatDate(value);
    }

    return String(value);
  };

  const labelize = (key) => {
    return String(key)
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^./, (c) => c.toUpperCase());
  };

  useEffect(() => {
    fetchPatient();
  }, [id]);

  if (loading) {
    return <Loader />;
  }

  if (!patient) {
    return (
      <Container className="mt-5 text-center">
        <h3>Patient Not Found</h3>
        <p className="text-muted">
          {error?.response?.data?.msg || error?.response?.data?.message || "The requested patient could not be found."}
        </p>
        <Button variant="primary" onClick={() => navigate('/patients')}>
          <ArrowBack className="me-2" /> Back to Patients
        </Button>
      </Container>
    );
  }

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <Container fluid>
          <Row className="mb-3">
            <Col sm={6}>
              <h1 className="h2 mb-0">Patient Details</h1>
              <div className="text-muted" style={{ marginTop: 4 }}>
                ID: {patient.id}
              </div>
            </Col>
            <Col sm={6}>
              <div className="d-flex justify-content-end align-items-center gap-2">
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={() => navigate('/patients')}
                >
                  <ArrowBack className="me-1" style={{ fontSize: '16px' }} />
                  Back to Patients
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => fetchPatient()}
                >
                  Refresh
                </Button>
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={handlePrint}
                >
                  <Print className="me-1" style={{ fontSize: '16px' }} />
                  Print
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="content">
        <Container fluid>
          <Row>
            {/* Left Column: Profile Info */}
            <Col md={3}>
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Patient Information</h5>
                </Card.Header>
                <Card.Body className="text-center">
                  <div className="mb-3">
                    {patient.patientPhoto ? (
                      <div onClick={() => setShowImagePreview(true)} style={{ cursor: 'pointer' }}>
                        <FilePreview
                          filePath={process.env.REACT_APP_BASE_URL + patient.patientPhoto}
                          altText="Patient Photo"
                        />
                      </div>
                    ) : (
                      <PermIdentity style={{ fontSize: "120px", color: "#6c757d" }} />
                    )}
                  </div>
                  <h4 className="mb-3">
                    {getFullName(patient)}
                  </h4>
                  <div className="mb-3">
                    <Badge bg={getStatusBadge(patient.status)}>
                      {(patient.status || 'Active').toUpperCase()}
                    </Badge>
                  </div>
                  <div className="text-start">
                    <div className="mb-2">
                      <strong>Patient ID:</strong> {patient.patientID || "N/A"}
                    </div>
                    <div className="mb-2">
                      <strong>Age:</strong> {patient.age || "N/A"}
                    </div>
                    <div className="mb-2">
                      <strong>Gender:</strong> {patient.gender === 101 ? "Male" : patient.gender === 102 ? "Female" : "N/A"}
                    </div>
                    <div className="mb-2">
                      <strong>Phone:</strong> {patient.patientPhoneNo || "N/A"}
                    </div>
                    <div className="mb-2">
                      <strong>Created At:</strong> {formatDate(patient.createdAt)}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Right Column: Incident and Treatment Details */}
            <Col md={9}>
              <Card ref={printableRef} className="h-100">
                <Card.Header>
                  <h5 className="mb-0">Incident and Treatment Details</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <strong>Incident Type:</strong><br />
                        <span className="text-muted">
                          {INCIDENT_TYPE.OTHER === patient.incidentType 
                            ? patient.incidentTypeOther 
                            : getIncidentTypeText(patient.incidentType)}
                        </span>
                      </div>
                      <div className="mb-3">
                        <strong>First Responder Treatment:</strong><br />
                        <span className="text-muted">
                          {TREATMENT_TYPES.OTHER === patient.firstResponderTreatments 
                            ? patient.firstResponderOtherTreatment 
                            : getTreatmentTypeText(patient.firstResponderTreatments)}
                        </span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <strong>Ambulance Treatment:</strong><br />
                        <span className="text-muted">
                          {TREATMENT_TYPES.OTHER === patient.ambulanceTreatments 
                            ? patient.ambulanceOtherTreatment 
                            : getTreatmentTypeText(patient.ambulanceTreatments)}
                        </span>
                      </div>
                      <div className="mb-3">
                        <strong>Last Updated:</strong><br />
                        <span className="text-muted">{formatDate(patient.updatedAt)}</span>
                      </div>
                    </Col>
                  </Row>

                  {patient.patientID && (
                    <div className="mt-4">
                      <h6>Uploaded Patient ID Document</h6>
                      <div className="border rounded p-3 text-center">
                        <FilePreview
                          filePath={process.env.REACT_APP_BASE_URL + patient.patientID}
                          altText="Patient ID Document"
                        />
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <h6>All Patient Fields</h6>
                    <div className="border rounded p-3" style={{ backgroundColor: "#fff" }}>
                      <Row>
                        {Object.keys(patient || {})
                          .sort((a, b) => String(a).localeCompare(String(b)))
                          .map((key) => {
                            const value = patient?.[key];
                            const isFileField = key === "patientPhoto" || key === "patientID";
                            if (isFileField) return null;

                            return (
                              <Col md={6} key={key} className="mb-2">
                                <div style={{ fontSize: "0.9rem" }}>
                                  <strong>{labelize(key)}:</strong>{" "}
                                  <span className="text-muted">{formatFieldValue(key, value)}</span>
                                </div>
                              </Col>
                            );
                          })}
                      </Row>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {showImagePreview && (
        <div
          className="image-preview-modal"
          onClick={() => setShowImagePreview(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <FilePreview
            filePath={process.env.REACT_APP_BASE_URL + patient.patientPhoto}
            altText="Patient Photo"
          />
        </div>
      )}
    </div>
  );
};

export default ViewPatient;